import moment from 'moment'
import { IScheduleSectionFormValues } from '@src/features/shift-post-v2/steps/shift-details-step/components/schedule-section/schedule-section.types'
import { INewDuplicateModalFormValues } from '@src/features/shift-post-v2/modals/new-duplicate-modal/new-duplicate-modal/new-duplicate-modal.form'
import { INewEditModalFormValues } from '@src/features/shift-post-v2/modals/new-edit-modal/new-edit-modal/new-edit-modal.form'
import { getFinalDay } from 'helpers/shiftHelpers'
import { TDay } from 'helpers/shiftHelpers/types'

// Map string weekdays to moment.js day numbers (0-6, where 0 is Sunday)
export const WEEKDAY_MAP: { [key: string]: number } = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
}

/**
 * Generates day objects for recurring shifts based on selected weekdays and date range
 * Generates ALL days in the range with proper working/non-working status
 *
 * @param startDay Start day of the range
 * @param endDay End day of the range
 * @param selectedWeekdays Array of weekday strings (e.g., ['Mon', 'Wed', 'Fri'])
 * @param values Form values
 */
export const generateRecurringDays = (
  startDay: Date,
  endDay: Date,
  selectedWeekdays: string[],
  values:
    | INewDuplicateModalFormValues
    | INewEditModalFormValues
    | IScheduleSectionFormValues,
) => {
  const days: TDay[] = []
  const startMoment = moment(startDay)
  const endMoment = moment(endDay)
  const currentMoment = startMoment.clone()

  // Convert string weekdays to numbers
  const selectedWeekdayNumbers = selectedWeekdays
    .map(day => WEEKDAY_MAP[day])
    .filter(num => num !== undefined)

  // Create a map of dirty days by date_start to preserve only user modifications and backend data
  const dirtyDaysMap = new Map<string, TDay>()
  values.days.forEach((day: TDay) => {
    if (day.is_dirty) {
      dirtyDaysMap.set(day.date_start, day)
    }
  })

  // Find the highest existing ID to start from
  const existingIds = values.days.map((day: TDay) => day.id)
  const maxExistingId = existingIds.length > 0 ? Math.max(...existingIds) : -1
  let nextId = maxExistingId + 1

  // Generate ALL days in the date range
  while (currentMoment.isSameOrBefore(endMoment)) {
    const dayOfWeek = currentMoment.day() // 0-6, where 0 is Sunday
    const isSelectedWeekday = selectedWeekdayNumbers.includes(dayOfWeek)

    const currentDayStart = currentMoment.clone()
    const currentDayEnd = getFinalDay(
      currentDayStart,
      values.hour_start,
      values.hour_end,
    )

    const date_start = currentDayStart.format('MM/DD/YYYY')
    const date_end = currentDayEnd.format('MM/DD/YYYY')

    const { date_ids } = values

    let day: TDay = {
      id: nextId++,
      day_start: currentDayStart,
      day_end: currentDayEnd,
      date_start,
      date_end,
      hour_start: values.hour_start,
      hour_end: values.hour_end,
      break: values.break,
      pay_value: values.pay_value,
      working: isSelectedWeekday, // Mark as working only if it's a selected weekday
      punch_cards_count: 0,
      existing: false,
      starting_day: false,
      is_dirty: false,
    }

    // Check if we have a dirty day with modifications to preserve
    const dirtyDay = dirtyDaysMap.get(date_start)
    if (dirtyDay) {
      day = {
        ...dirtyDay,
        existing: dirtyDay.existing || false,
        is_dirty: true, // Preserve the dirty day's properties, especially working status and ID
      }
    } else if (date_ids[date_start]) {
      // Fall back to date_ids if no dirty day (this is for existing backend data)
      day.id = date_ids[date_start].id
      day.working = date_ids[date_start].working
      day.punch_cards_count = date_ids[date_start].punch_cards_count
      day.existing = true
      day.starting_day = false
      day.is_dirty = true // Backend data should be considered dirty to preserve it
      day = {
        ...day,
        id: date_ids[date_start].id,
        working: date_ids[date_start].working,
        punch_cards_count: date_ids[date_start].punch_cards_count,
        existing: true, // Backend data should be considered dirty to preserve it
      }
    }

    days.push(day)
    currentMoment.add(1, 'day')
  }

  return days
}
